import {ProductType} from '@wix/wixstores-client-core';
import type {ICurrentCartService, SiteStore} from '@wix/wixstores-client-storefront-sdk';
import {CreateCheckoutExceptions, FedopsInteractions} from '../../common/constants';
import {ApplicationError, CartLayout, ICartControllerApi, ModelContext} from '../../types/app.types';
import {BIService} from './BIService';
import {StyleSettingsService} from './StyleSettingsService';
import {SPECS} from '../specs';
import {CartModel} from '../models/Cart/Cart.model';
import {LineItemModel} from '../models/LineItems/LineItem.model';
import {CartSummaryModel} from '../models/CartSummary/CartSummary.model';
import {ViolationSeverity} from '../models/CartSummary/Violation.model';
import {
  Cart as CartV1,
  ChannelType,
  DeliveryLogistics,
  EstimateTotalsResponse,
  MultiCurrencyPrice,
} from '@wix/auto_sdk_ecom_current-cart';
import {Cart, CartSummary, ValidationsConfig} from '@wix/auto_sdk_ecom_current-cart-v-2';
import {EcommerceSettingsService} from './EcommerceSettingsService';
import type {DeliveryOptionsService} from '@wix/delivery-section-cart/services';
import {DeliverySectionModel} from '../models/DeliverySection.model';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {getFormattingFunction} from '../utils/getFormattingFunction';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {AddCouponErrorModel} from '../models/AddCouponError.model';

type CartWithPrivateFields = CartV1 & {
  subtotalAfterDiscounts: MultiCurrencyPrice;
  subtotal: MultiCurrencyPrice;
  discount: MultiCurrencyPrice;
  paymentCurrency: string;
};
type DeliveryLogisticsWithPrivateFields = DeliveryLogistics & {deliveryTimeSlot: string};

export class CartService {
  private readonly siteStore: SiteStore;
  private readonly viewerContext: ViewerContext;
  private readonly biService: BIService;
  private readonly styleSettingsService: StyleSettingsService;
  private readonly currentCartService: ICurrentCartService;
  private readonly ecommerceSettingsService: EcommerceSettingsService;
  private readonly deliveryOptionsServicePromise: Promise<DeliveryOptionsService>;
  public readonly origin: string;
  public readonly cartLayout: CartLayout;
  public couponError: AddCouponErrorModel = null;
  public sdkCartForDeliverySection?: Cart;
  public cartModel: CartModel;
  public cartSummary: CartSummaryModel;
  public checkoutId: string;
  public hasError: boolean;
  public isSummaryUpdating: boolean = false;
  public isCouponUpdateInProgress: boolean = false;
  public isSummaryLoading: boolean = false;
  public controllerApi: ICartControllerApi;
  public loadingItems: string[] = [];
  public errorOnEstimateTotals: boolean = false;
  public deliverySectionModel: DeliverySectionModel;
  public controllerParams: ControllerParams;

  constructor({
    siteStore,
    biService,
    styleSettingsService,
    currentCartService,
    origin,
    controllerApi,
    ecommerceSettingsService,
    cartLayout,
    deliveryOptionsServicePromise,
    controllerParams,
    viewerContext,
  }: {
    controllerApi: ICartControllerApi;
    siteStore: SiteStore;
    biService: BIService;
    styleSettingsService: StyleSettingsService;
    currentCartService: ICurrentCartService;
    origin: string;
    ecommerceSettingsService: EcommerceSettingsService;
    cartLayout: CartLayout;
    deliveryOptionsServicePromise: Promise<DeliveryOptionsService>;
    controllerParams: ControllerParams;
    viewerContext: ViewerContext;
  }) {
    this.siteStore = siteStore;
    this.biService = biService;
    this.styleSettingsService = styleSettingsService;
    this.currentCartService = currentCartService;
    this.origin = origin;
    this.controllerApi = controllerApi;
    this.ecommerceSettingsService = ecommerceSettingsService;
    this.cartLayout = cartLayout;
    this.deliveryOptionsServicePromise = deliveryOptionsServicePromise;
    this.controllerParams = controllerParams;
    this.viewerContext = viewerContext;
  }

  public clearLoadingItems = () => {
    this.loadingItems = [];
  };

  public showLoaderOnItem = (cartItemId: string) => {
    this.loadingItems.push(cartItemId);
    this.isSummaryUpdating = true;
  };

  public async fetchCartV2(): Promise<void> {
    const [cart] = await Promise.all([this.currentCartService.getCurrentCart(), this.ecommerceSettingsService.init()]);
    this.setCart(cart);
  }

  public async fetchCartV1(): Promise<void> {
    const [cart] = await Promise.all([
      this.currentCartService.getCurrentCartV1(),
      this.ecommerceSettingsService.init(),
    ]);
    this.setCartFromV1(cart);
  }

  public async isPreselectedNonShippingFlow(): Promise<boolean> {
    try {
      const estimatedTotals = await this.currentCartService.estimateCurrentCartTotalsV1({
        calculateShipping: true,
        calculateTax: this.shouldCalculateTax(),
        calculateAdditionalFees: this.shouldCalculateAdditionalFees(),
      });
      const isPickup =
        !!estimatedTotals.shippingInfo?.selectedCarrierServiceOption?.logistics?.pickupDetails?.address?.addressLine1;
      const hasTimeSlots = Boolean(
        (estimatedTotals.shippingInfo?.selectedCarrierServiceOption?.logistics as DeliveryLogisticsWithPrivateFields)
          ?.deliveryTimeSlot
      );
      return isPickup || hasTimeSlots;
    } catch (e) {
      await this.controllerApi.reportFedops(FedopsInteractions.errorInEstimateTotalsWithShipping);
      return false;
    }
  }

  public async fetchCartSummary(): Promise<void> {
    try {
      if (this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)) {
        const {summary} = await this.currentCartService.estimateCurrentCart({
          validationConfig: ValidationsConfig.PRE_CHECKOUT_VALIDATIONS,
          calculateDelivery: this.shouldCalculateDelivery(),
          calculateTax: this.shouldCalculateTax(),
          calculateAdditionalFees: this.shouldCalculateAdditionalFees(),
          calculateGiftCards: false,
          calculateOrderDiscounts: true,
        });
        this.setCartSummaryModel(summary);
        this.biService.checkoutCartCartSummaryPresented(this.cartModel, this.cartSummary);
        if (this.shouldCalculateDelivery()) {
          const deliveryOptionsService = await this.deliveryOptionsServicePromise;
          const options = await deliveryOptionsService.getDeliveryOptions({cartV2: this.sdkCartForDeliverySection});
          this.deliverySectionModel = new DeliverySectionModel(
            options,
            this.sdkCartForDeliverySection.deliveryInfo?.method?.code
          );
        }
      } else {
        const estimatedTotals = await this.currentCartService.estimateCurrentCartTotalsV1({
          calculateShipping: this.shouldCalculateDelivery(),
          calculateTax: this.shouldCalculateTax(),
          calculateAdditionalFees: this.shouldCalculateAdditionalFees(),
        });
        this.setEstimatedTotalV1Model(estimatedTotals);
        if (this.shouldCalculateDelivery()) {
          const deliveryOptionsService = await this.deliveryOptionsServicePromise;
          const options = await deliveryOptionsService.getDeliveryOptions(
            {estimateTotals: estimatedTotals},
            estimatedTotals.shippingInfo?.selectedCarrierServiceOption?.code
          );
          this.deliverySectionModel = new DeliverySectionModel(
            options,
            estimatedTotals.shippingInfo?.selectedCarrierServiceOption?.code
          );
        }
      }

      this.errorOnEstimateTotals = false;
      await this.controllerApi.updateComponent();
    } catch {
      this.biService.cartEstimateTotalsErrorShown(this.cartModel, this.cartLayout);
      this.errorOnEstimateTotals = true;
      await this.controllerApi.updateComponent();
    }
  }

  public get hasPickupOption(): boolean {
    return this.deliverySectionModel?.options.some((option) => option.isPickup);
  }

  private sendCartLoadedBi() {
    return this.biService.viewCartPageSf({
      cartLayout: this.cartLayout,
      cartModel: this.cartModel,
      shouldShowCoupon: this.styleSettingsService.shouldShowCoupon,
      shouldShowBuyerNote: this.styleSettingsService.shouldShowBuyerNote,
      shouldShowContinueShopping: this.styleSettingsService.shouldRenderContinueShopping,
      shouldShowShipping: this.styleSettingsService.shouldShowShipping,
      shouldShowTax: this.styleSettingsService.shouldShowTax,
      hasPickupOption: this.hasPickupOption,
      isCheckoutButtonPresented: this.styleSettingsService.isCheckoutButtonShowInSomeBreakpoint,
      isViewCartButtonPresented: this.styleSettingsService.isViewCartButtonShownInSomeBreakpoint,
      hasGiftCardLineItem: this.cartModel.lineItemsTypes.includes(ProductType.GIFT_CARD),
    });
  }

  public async fetchCartAndEstimateTotals(isInitialLoad = false): Promise<void> {
    /* istanbul ignore next */
    if (this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)) {
      await this.fetchCartV2();
    } else {
      await this.fetchCartV1();
    }
    const shouldEstimateTotals = this.shouldEstimateTotals();
    this.isSummaryLoading = shouldEstimateTotals && isInitialLoad;
    this.isSummaryUpdating = shouldEstimateTotals;

    if (!this.viewerContext.isSSR() && shouldEstimateTotals) {
      this.controllerParams.flowAPI.panoramaClient?.transaction(FedopsInteractions.estimateTotals).start();
      void this.fetchCartSummary().then(() => {
        this.controllerParams.flowAPI.panoramaClient?.transaction(FedopsInteractions.estimateTotals).finish();
        this.isSummaryUpdating = false;
        this.isSummaryLoading = false;
        void this.controllerApi.updateComponent();
        void this.sendCartLoadedBi();
      });
    } else {
      this.removeEstimatedTotals();
      void this.sendCartLoadedBi();
    }
  }

  private shouldCalculateTax(): boolean {
    const {shouldShowTax} = this.styleSettingsService;
    const pricesIncludeTax = this.cartModel.taxInfo.pricesIncludeTax;
    return shouldShowTax && !pricesIncludeTax;
  }

  public shouldCalculateDelivery(): boolean {
    const {shouldShowShipping} = this.styleSettingsService;
    return shouldShowShipping && !this.isNonShippableCart;
  }

  private shouldCalculateAdditionalFees(): boolean {
    return this.cartLayout === CartLayout.CART;
  }

  private isCartWithDepositItem(): boolean {
    return this.cartModel.lineItems.some((lineItem) => lineItem.paymentConfig.depositAmount);
  }

  private hasAnActiveSPI() {
    const {hasValidationsInCart, hasAdditionalFees} = this.ecommerceSettingsService.getEcommerceSettings();
    return hasValidationsInCart || (hasAdditionalFees && this.shouldCalculateAdditionalFees());
  }

  public readonly clearError = async () => {
    this.setHasErrorState(false);
    await this.controllerApi.updateComponent();
  };

  public readonly handleGeneralError = async (error: Error) => {
    this.clearLoadingItems();
    this.setHasErrorState(true);
    this.isSummaryUpdating = false;
    await this.controllerApi.updateComponent();
    this.biService.errorPresentedInCartSideCart(error.message, this.cartModel);
    setTimeout(() => {
      void (async () => {
        await this.clearError();
      })();
    }, 5000);
  };

  public shouldEstimateTotals(): boolean {
    const {isExpressCheckoutButtonShownInSomeBreakpointOrIsEditorMode} = this.styleSettingsService;
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    const editorMode = moveCartToViewerContext
      ? this.viewerContext.getViewMode() === 'Editor'
      : this.siteStore.isEditorMode();

    const shouldEstimateTotals =
      this.cartModel.lineItems.length > 0 &&
      (this.shouldCalculateDelivery() ||
        this.shouldCalculateTax() ||
        isExpressCheckoutButtonShownInSomeBreakpointOrIsEditorMode ||
        this.isCartWithDepositItem() ||
        editorMode ||
        this.hasAnActiveSPI() ||
        this.cartModel.hasItemWithFreeTrial ||
        !!this.cartModel.coupon);

    if (shouldEstimateTotals) {
      void this.biService.shouldEstimateTotalsTestEvent({
        cartLayout: this.cartLayout,
        shouldCalculateShipping: this.shouldCalculateDelivery(),
        shouldCalculateTax: this.shouldCalculateTax(),
        isExpressCheckoutButtonShownInSomeBreakpointOrIsEditorMode,
        isCartWithDepositItem: this.isCartWithDepositItem(),
        editorMode,
        hasValidationsInCart: this.ecommerceSettingsService.getEcommerceSettings().hasValidationsInCart,
        hasAdditionalFees:
          this.ecommerceSettingsService.getEcommerceSettings().hasAdditionalFees && this.cartLayout === CartLayout.CART,
        hasItemWithFreeTrial: this.cartModel.hasItemWithFreeTrial,
      });
    }

    return shouldEstimateTotals;
  }

  public get isNonShippableCart(): boolean {
    return !this.hasShippableItems;
  }

  public get hasShippableItems(): boolean {
    return this.cartModel?.lineItems.some((lineItem) => lineItem.attributes.physicalProperties.shippable);
  }

  public get isZeroCart(): boolean {
    if (this.cartSummary) {
      return this.cartSummary.priceSummary.total.convertedAmount === 0;
    } else {
      return this.cartModel.subtotal.convertedAmount === 0;
    }
  }

  public get isEmpty(): boolean {
    return !this.cartModel?.lineItems.length;
  }

  public get areAllItemsInStock(): boolean {
    return (
      this.cartModel?.lineItems &&
      this.cartModel.lineItems.every((lineItem) =>
        this.viewerContext.isExperimentEnabled(SPECS.FixOutOfStockOnCart)
          ? lineItem.quantityInfo.quantity > 0
          : lineItem.quantityInfo.availableQuantity === undefined || lineItem.quantityInfo.availableQuantity > 0
      )
    );
  }

  public get isFullAddressRequired() {
    return (
      this.deliverySectionModel?.errors?.hasShippingDestinationError ||
      this.deliverySectionModel?.errors?.hasShippingZipCodeError
    );
  }

  public get itemsCount(): number {
    return this.cartModel.lineItems.reduce((count, lineItem) => count + lineItem.quantityInfo.quantity, 0);
  }

  public get hasErrorViolations(): boolean {
    return this.cartSummary?.violations?.some((violation) => violation.severity === ViolationSeverity.error);
  }

  public async createCheckout(): Promise<string | {error: string} | undefined> {
    try {
      const {checkoutId} = await this.currentCartService.createCheckoutV1({channelType: ChannelType.WEB});
      this.checkoutId = checkoutId;
      return checkoutId;
    } catch (error) {
      if (
        (error as ApplicationError)?.details?.applicationError?.code === CreateCheckoutExceptions.siteMustAcceptPayments
      ) {
        return {error: CreateCheckoutExceptions.siteMustAcceptPayments};
      }
      return undefined;
    }
  }

  public readonly updateItemQuantity = async (lineItemId: string, newQuantity: number): Promise<void> => {
    try {
      if (this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)) {
        await this.currentCartService.updateLineItemsInCurrentCart({
          lineItems: [{lineItemId, quantity: {newQuantity}}],
        });
        void this.biService.updateCartItemQuantity(this.cartModel);
      } else {
        return await this.currentCartService.updateLineItemQuantityV1(
          {lineItemId, quantity: newQuantity},
          {origin: this.origin}
        );
      }
    } catch (e) {
      await this.handleGeneralError(e as Error);
      await this.refreshCart();
    }
  };

  public readonly refreshCart = async (): Promise<void> => {
    await this.currentCartService.refreshCurrentCart();
  };

  public readonly updateBuyerNote = async (content: string) => {
    try {
      if (this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)) {
        await this.currentCartService.updateCurrentCart({note: content});
      } else {
        await this.currentCartService.updateBuyerNoteV1({buyerNote: content});
      }
      this.biService.updateBuyerNote(this.cartModel, !!content);
    } catch (e) {
      await this.handleGeneralError(e as Error);
    }
  };

  public readonly removeItemFromCart = async (lineItem: LineItemModel): Promise<void> => {
    try {
      if (this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)) {
        await this.currentCartService.removeLineItemsFromCurrentCart({lineItemIds: [lineItem.id]});
        void this.biService.removeLineItemFromCurrentCart(this.cartModel);
      } else {
        return await this.currentCartService.removeLineItemV1({lineItemId: lineItem.id}, {origin: this.origin});
      }
    } catch (e) {
      await this.handleGeneralError(e as Error);
    }
  };

  private getAddressForSetAddress({
    country,
    subdivision,
    postalCode,
  }: {
    country: string;
    subdivision?: string;
    postalCode?: string;
  }) {
    return {
      country,
      subdivision: subdivision ?? null,
      postalCode: postalCode ?? null,
      addressLine1: null,
      addressLine2: null,
      city: null,
      streetAddress: null,
      location: null,
    };
  }

  public readonly setAddress = async ({
    country,
    subdivision,
    postalCode,
  }: {
    country: string;
    subdivision?: string;
    postalCode?: string;
  }): Promise<void> => {
    try {
      if (this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)) {
        await this.currentCartService.updateCurrentCart({
          deliveryInfo: {
            address: this.getAddressForSetAddress({country, subdivision, postalCode}),
          },
          paymentInfo: {
            billingAddress: this.getAddressForSetAddress({country, subdivision, postalCode}),
          },
        });
      } else {
        return await this.currentCartService.setAddressV1({address: {country, subdivision, zipCode: postalCode}});
      }
    } catch (e) {
      await this.handleGeneralError(e as Error);
    }
  };

  public readonly setShippingOption = async ({
    deliveryMethod,
  }: {
    deliveryMethod: {code: string; appId: string};
  }): Promise<void> => {
    try {
      if (this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)) {
        await this.currentCartService.setDeliveryMethodForCurrentCart({deliveryMethod});
      } else {
        return await this.currentCartService.selectShippingOptionV1({
          option: {code: deliveryMethod.code, carrierId: deliveryMethod.appId},
        });
      }
    } catch (e) {
      await this.handleGeneralError(e as Error);
    }
  };

  public readonly clearCouponError = (): void => {
    this.couponError = null;
  };

  public readonly addCoupon = async (couponCode: string): Promise<void> => {
    try {
      !this.viewerContext.isExperimentEnabled(SPECS.FixCouponErrorInCart) && this.clearCouponError();
      this.isSummaryUpdating = true;
      this.isCouponUpdateInProgress = true;
      await this.controllerApi.updateComponent();
      await this.currentCartService.addCouponToCurrentCart({coupon: {code: couponCode}});
      this.biService.addCouponToCurrentCart(this.cartModel, couponCode);
      this.isCouponUpdateInProgress = false;
      await this.controllerApi.updateComponent();
    } catch (e) {
      this.couponError = AddCouponErrorModel.fromCartV2(e, {
        t: this.controllerParams.flowAPI.translations.t,
        modelContext: this.getModelContext(
          this.cartModel.customerInfo.currencyCode,
          this.cartModel.paymentInfo.currencyCode
        ),
      });
      this.biService.errorWhenApplyingACouponSf(this.cartModel, couponCode, this.couponError.code);
      this.isSummaryUpdating = false;
      this.isCouponUpdateInProgress = false;
      return this.controllerApi.updateComponent();
    }
  };

  public readonly applyCouponV1 = async (couponCode: string): Promise<void> => {
    try {
      !this.viewerContext.isExperimentEnabled(SPECS.FixCouponErrorInCart) && this.clearCouponError();
      this.isSummaryUpdating = true;
      this.isCouponUpdateInProgress = true;
      await this.controllerApi.updateComponent();
      const applyCouponPromise = this.currentCartService.applyCouponV1({code: couponCode}, {origin: this.origin});
      await applyCouponPromise.catch((e) => {
        this.couponError = AddCouponErrorModel.fromCartV1(e, this.controllerParams.flowAPI.translations.t);
        this.biService.errorWhenApplyingACouponSf(this.cartModel, couponCode, this.couponError.code);
        throw e;
      });
      this.isCouponUpdateInProgress = false;
      await this.controllerApi.updateComponent();
    } catch {
      this.isSummaryUpdating = false;
      this.isCouponUpdateInProgress = false;
      return this.controllerApi.updateComponent();
    }
  };

  public readonly removeCoupon = async (): Promise<void> => {
    try {
      this.isSummaryUpdating = true;
      this.isCouponUpdateInProgress = true;
      await this.controllerApi.updateComponent();

      if (this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCartComponents)) {
        const couponCode = this.cartModel.coupon?.code;
        await this.currentCartService.removeCouponFromCurrentCart({couponId: this.cartModel.coupon.id});
        void this.biService.removeCouponFromCurrentCart(this.cartModel, couponCode);
      } else {
        await this.currentCartService.removeCouponV1({origin: this.origin});
      }
      this.isCouponUpdateInProgress = false;
      await this.controllerApi.updateComponent();
    } catch (e) {
      this.isCouponUpdateInProgress = false;
      await this.controllerApi.updateComponent();
      await this.handleGeneralError(e as Error);
    }
  };

  public readonly setHasErrorState = (value: boolean) => (this.hasError = value);

  private clearCouponErrorWhenChanged(prevCart?: CartModel, nextCart?: CartModel): void {
    if (prevCart?.coupon?.code !== nextCart?.coupon?.code) {
      this.clearCouponError();
    }
  }

  /* istanbul ignore next */
  private readonly setCart = (cart: Cart) => {
    const prevCart = this.cartModel;
    this.sdkCartForDeliverySection = cart;
    this.cartModel = CartModel.fromCartV2(
      cart,
      this.getModelContext(cart.customerInfo?.currencyCode, cart.paymentInfo?.currencyCode)
    );
    this.viewerContext.isExperimentEnabled(SPECS.FixCouponErrorInCart) &&
      this.clearCouponErrorWhenChanged(prevCart, this.cartModel);
  };
  private readonly setCartFromV1 = (cart: CartV1) => {
    const prevCart = this.cartModel;
    this.cartModel = CartModel.fromCartV1(
      cart as CartWithPrivateFields,
      this.getModelContext(cart.conversionCurrency, cart.paymentCurrency)
    );
    this.viewerContext.isExperimentEnabled(SPECS.FixCouponErrorInCart) &&
      this.clearCouponErrorWhenChanged(prevCart, this.cartModel);
  };

  private readonly setCartSummaryModel = (cartSummary: CartSummary) => {
    this.cartSummary = CartSummaryModel.fromCartV2(
      cartSummary,
      this.getModelContext(this.cartModel.customerInfo.currencyCode, this.cartModel.paymentInfo.currencyCode)
    );
  };

  private readonly setEstimatedTotalV1Model = (estimatedTotals: EstimateTotalsResponse) =>
    (this.cartSummary = CartSummaryModel.fromCartV1(
      estimatedTotals,
      this.getModelContext(this.cartModel.customerInfo.currencyCode, this.cartModel.paymentInfo.currencyCode)
    ));
  private readonly removeEstimatedTotals = () => (this.cartSummary = undefined);

  private readonly getModelContext = (formatingCurrency: string, paymentCurrency: string): ModelContext => {
    return {
      viewerContext: this.viewerContext,
      formatAmount: getFormattingFunction({flowAPI: this.controllerParams.flowAPI, formatingCurrency}),
      shouldPayWithConvertedCurrency: paymentCurrency === formatingCurrency,
      taxNameOld: this.siteStore.taxName,
      baseUrl: this.viewerContext.isExperimentEnabled(SPECS.UsePartialLineItemUrlInCart)
        ? this.controllerParams.controllerConfig.wixCodeApi.location.baseUrl
        : undefined,
    };
  };

  public reportCheckoutButtonPresented(isButtonShown: boolean): void {
    this.biService.checkoutButtonPresentedToUoU(this.cartModel, isButtonShown);
  }

  public reportCartButtonPresented(isButtonShown: boolean): void {
    this.biService.cartButtonPresentedToUoU(this.cartModel, isButtonShown);
  }

  public reportPromoCodePresented(isPromoCodeShown: boolean): void {
    this.biService.promoCodePresentedToUoU(this.cartModel, isPromoCodeShown);
  }

  public reportBuyerNotePresented(isBuyerNoteShown: boolean): void {
    this.biService.buyerNotePresentedToUoU(this.cartModel, isBuyerNoteShown);
  }
}
